<template>
  <div>
    <p v-if="editModeActive && yearsExperience === 0" class="text-caption text-grey-text-subdued -mt-16 mb-8">
      Stand out by adding your years of experience.
    </p>
    <poppins-button
      v-if="editModeActive && !hasAnyExperiences"
      :href="shouldUseSpaRoutes ? null : editLink"
      :to="shouldUseSpaRoutes ? editLink : null"
      variant="text"
      size="medium"
      value="Add experience"
    />
    <icon-list v-else-if="isChildCare" :items="ageGroupDisplay" icon-color="#4B4C52" :custom-margin="16" />
    <tag-label-group v-else-if="isPetCare" :items="petExperienceDisplay" />
    <tag-label-group v-else-if="isHousekeeping" :items="serviceExperienceDisplay" />
    <div v-else-if="isCompanionCare">
      <checkmark-list v-if="ageGroupsExperience" :tips="companionCareAgeGroupDisplay" />
      <tag-label-group
        v-if="basicCareServicesExperience"
        :items="basicServicesExperienceDisplay"
        title="Basic services"
        class="mt-16"
      />
      <tag-label-group
        v-if="personalCareServicesExperience"
        :items="personalServicesExperienceDisplay"
        title="Personal services"
        class="mt-16"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { isChildCare, isPetCare, isHousekeeping, isCompanionCare } from '../../__types__/CareTypes';
import { experienceMap, ExperienceDisplay } from '../../__types__/ExperienceTypes';
import IconList from '@/components/icon_list/IconList.vue';
import TagLabelGroup from '@/components/tag_label_group/TagLabelGroup.vue';
import CheckmarkList from '@/components/checkmark_list/CheckmarkList.vue';
import PoppinsButton from '@/components/button/Button.vue';

export default {
  name: 'ProfileExperience',
  components: { IconList, TagLabelGroup, CheckmarkList, PoppinsButton },
  props: {
    careType: {
      type: String,
      default: '',
    },
    ageGroups: {
      type: Array as () => string[],
      default: () => [],
    },
    experienceCaringFor: {
      type: Array as () => string[],
      default: () => [],
    },
    servicesExperience: {
      type: Array as () => string[],
      default: () => [],
    },
    ageGroupsExperience: {
      type: Array as () => string[],
      default: () => [],
    },
    basicCareServicesExperience: {
      type: Array as () => string[],
      default: () => [],
    },
    personalCareServicesExperience: {
      type: Array as () => string[],
      default: () => [],
    },
    hasAnyExperiences: {
      type: Boolean,
      default: false,
    },
    editModeActive: {
      type: Boolean,
      default: false,
    },
    yearsExperience: {
      type: Number,
      default: 0,
    },
    editLink: {
      type: String,
      default: '',
    },
    shouldUseSpaRoutes: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      isChildCare: isChildCare(this.careType),
      isPetCare: isPetCare(this.careType),
      isHousekeeping: isHousekeeping(this.careType),
      isCompanionCare: isCompanionCare(this.careType),
    };
  },
  computed: {
    ageGroupDisplay(): ExperienceDisplay[] {
      return this.mapExperienceKeys(this.ageGroups, 'ageGroups');
    },
    petExperienceDisplay(): ExperienceDisplay[] {
      return this.mapExperienceKeys(this.experienceCaringFor, 'petExperience');
    },
    serviceExperienceDisplay(): ExperienceDisplay[] {
      return this.mapExperienceKeys(this.servicesExperience, 'services');
    },
    companionCareAgeGroupDisplay(): ExperienceDisplay[] {
      return this.mapExperienceKeys(this.ageGroupsExperience, 'companionCareAges');
    },
    basicServicesExperienceDisplay(): ExperienceDisplay[] {
      return this.mapExperienceKeys(this.basicCareServicesExperience, 'basicCare');
    },
    personalServicesExperienceDisplay(): ExperienceDisplay[] {
      return this.mapExperienceKeys(this.personalCareServicesExperience, 'personalCare');
    },
  },
  methods: {
    mapExperienceKeys(keys: string[], mapType: keyof typeof experienceMap): ExperienceDisplay[] {
      return keys.map((key) => experienceMap[mapType]?.[key]).filter((item): item is ExperienceDisplay => !!item);
    },
  },
};
</script>
